import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { alertLevel, isProminent, sortAlerts } from "../../shared/alerts";
import type { LayoutDoc } from "../../shared/layout";
import type { WeatherAlert } from "../../shared/models";
import { readPref, writePref } from "../data/persist";
import { useAlerts } from "../data/weatherQueries";

/** The Alerts screen's "Got it" button asks the director to stand down. */
export const ALERTS_DISMISS_EVENT = "km:alerts-dismiss";

export function dismissTakeover() {
  window.dispatchEvent(new Event(ALERTS_DISMISS_EVENT));
}

/** The screen to show for alerts: the Alerts screen, else Weather & sky. */
export function alertsLayoutOf(layouts: LayoutDoc[]): LayoutDoc | undefined {
  return layouts.find((l) => l.presetKey === "alerts") ?? layouts.find((l) => l.presetKey === "weather-focus");
}

/**
 * Severe-weather takeover for wall displays: when a life-threatening alert
 * (tornado, severe storm, flash flood, red flag…) starts, switch to the
 * Alerts screen; when it ends or someone taps "Got it", go back to where the
 * wall was. Dismissals are per device and per alert, so a new warning
 * switches again.
 */
export function useAlertDirector(opts: { enabled: boolean; layouts: LayoutDoc[]; activeId: string | undefined; editing: boolean; select: (id: string) => void }) {
  const q = useAlerts(true);
  const alerts = useMemo(() => sortAlerts(q.data?.alerts ?? []), [q.data]);
  const [dismissed, setDismissed] = useState<string[]>(() => readPref<string[]>("alertsDismissed", []));
  const returnTo = useRef<string | null>(readPref<string | null>("alertsReturnTo", null));
  const target = alertsLayoutOf(opts.layouts);

  const pending = alerts.filter((a) => alertLevel(a) === "takeover" && !dismissed.includes(a.id));
  const pendingKey = pending.map((a) => a.id).join("|");

  const setReturn = (id: string | null) => {
    returnTo.current = id;
    writePref("alertsReturnTo", id);
  };

  useEffect(() => {
    if (!opts.enabled || !target || opts.editing) return;
    if (pending.length > 0 && opts.activeId !== target.id) {
      if (!returnTo.current && opts.activeId) setReturn(opts.activeId);
      // Close open dialogs first (each listens for Escape), then switch.
      for (let i = 0; i < 5 && document.querySelector(".sheet"); i++) window.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
      opts.select(target.id);
    } else if (pending.length === 0 && returnTo.current) {
      const back = returnTo.current;
      setReturn(null);
      if (opts.activeId === target.id && opts.layouts.some((l) => l.id === back)) opts.select(back);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKey, opts.enabled, target?.id, opts.editing]);

  const dismiss = useCallback(() => {
    setDismissed((prev) => {
      // Keep only ids still active, so the list never grows without bound.
      const active = new Set(alerts.map((a) => a.id));
      const next = [...new Set([...prev.filter((id) => active.has(id)), ...alerts.filter((a) => alertLevel(a) === "takeover").map((a) => a.id)])];
      writePref("alertsDismissed", next);
      return next;
    });
  }, [alerts]);

  useEffect(() => {
    window.addEventListener(ALERTS_DISMISS_EVENT, dismiss);
    return () => window.removeEventListener(ALERTS_DISMISS_EVENT, dismiss);
  }, [dismiss]);

  const prominent: WeatherAlert[] = alerts.filter(isProminent);
  return { alerts, prominent, takeoverPending: pending.length > 0, target };
}
